import { cn } from "@jesus-central-church/shared";
import { AnimatePresence, motion } from "motion/react";
import * as React from "react";
import { Image } from "../media/image";

export type DonationFrequency = "oneTime" | "monthly";

export interface DonationCardProperties {
  /** Available donation purposes */
  purposes: string[];
  /** Default frequency selection */
  defaultFrequency?: DonationFrequency;
  /** Background color CSS variable name (e.g., "background" for var(--background)) */
  backgroundColor?: string;
  /** Primary color CSS variable name (e.g., "strategy-red" for var(--strategy-red)) */
  primaryColor?: string;
  /** Text color CSS variable name (e.g., "foreground" for var(--foreground)) */
  textColor?: string;
  /** Card title */
  title: string;
  /** Card description (can be string or ReactNode for rich text) */
  description?: string | React.ReactNode;
  /** Available donation amount options */
  options: number[];
  /** Optional image to display (e.g., ANBI logo) */
  image?: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
  /** Maximum amount for one-time donations (default: 50000) */
  maxAmountOneTime?: number;
  /** Maximum amount for monthly donations (default: 1000) */
  maxAmountMonthly?: number;
  /** Callback when form is submitted with valid data */
  onSubmit?: (data: {
    amount: number;
    purpose: string;
    note: string;
    recurring: DonationFrequency;
    name: string;
    email: string;
  }) => Promise<void> | void;
  /** Optional initial values */
  initialValues?: {
    amount?: number;
    purpose?: string;
    note?: string;
    recurring?: DonationFrequency;
  };
  /** Custom className for the section */
  className?: string;
}

// ------ Helpers ------
const stepVariants = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, x: -24, transition: { duration: 0.2 } },
};

export function DonationCard({
  purposes,
  defaultFrequency = "oneTime",
  backgroundColor = "background",
  primaryColor = "strategy-red",
  textColor = "foreground",
  title,
  description,
  options,
  image,
  maxAmountOneTime = 50_000,
  maxAmountMonthly = 1000,
  onSubmit,
  initialValues,
  className,
}: DonationCardProperties) {
  const [step, setStep] = React.useState<1 | 2>(1);

  // step 1 - initialize from initialValues or defaults
  const [amount, setAmount] = React.useState<number | "">(
    initialValues?.amount ?? 25,
  );
  const [purpose, setPurpose] = React.useState<string>(
    initialValues?.purpose || purposes?.[0] || "",
  );
  const [note, setNote] = React.useState<string>(initialValues?.note || "");
  const [recurring, setRecurring] = React.useState<DonationFrequency>(
    initialValues?.recurring || defaultFrequency || "oneTime",
  );

  // step 2 (billingAddress) - never saved to initial values
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [touchedFields, setTouchedFields] = React.useState<{
    amount: boolean;
    purpose: boolean;
    name: boolean;
    email: boolean;
  }>({
    amount: false,
    purpose: false,
    name: false,
    email: false,
  });

  const amounts = options?.length ? options : [10, 25, 50, 100];

  // Default purposes or use from props
  const donationPurposes = purposes;

  function getMaxAmount() {
    return recurring === "monthly" ? maxAmountMonthly : maxAmountOneTime;
  }

  function exceedsMaxAmount() {
    if (typeof amount !== "number") return false;
    return amount > getMaxAmount();
  }

  function isValidName() {
    return name.trim().length >= 2;
  }

  function isValidEmail() {
    if (!email.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  function isValidAmount() {
    if (typeof amount !== "number") return false;
    return amount > 0 && amount <= getMaxAmount();
  }

  function isValidPurpose() {
    return purpose.trim().length > 0;
  }

  function getAmountError() {
    if (!touchedFields.amount) return null;
    if (typeof amount !== "number" || amount <= 0) {
      return "Voer een bedrag in";
    }
    if (exceedsMaxAmount()) {
      return `Maximum bedrag voor ${recurring === "monthly" ? "maandelijkse" : "eenmalige"} donaties is €${getMaxAmount().toLocaleString("nl-NL")}`;
    }
    return null;
  }

  function getPurposeError() {
    if (!touchedFields.purpose) return null;
    if (!isValidPurpose()) {
      return "Selecteer een bestemming";
    }
    return null;
  }

  function getNameError() {
    if (!touchedFields.name) return null;
    if (!name.trim()) {
      return "Naam is verplicht";
    }
    if (name.trim().length < 2) {
      return "Naam moet minimaal 2 tekens bevatten";
    }
    return null;
  }

  function getEmailError() {
    if (!touchedFields.email) return null;
    if (!email.trim()) {
      return "Email is verplicht";
    }
    if (!isValidEmail()) {
      return "Voer een geldig emailadres in";
    }
    return null;
  }

  function isValidStep1() {
    return isValidAmount() && isValidPurpose();
  }

  function isValidStep2() {
    return isValidName() && isValidEmail() && isValidAmount();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Mark all fields as touched to show validation errors
    setTouchedFields({
      amount: true,
      purpose: true,
      name: true,
      email: true,
    });

    if (!isValidStep2()) {
      setError("Controleer de ingevulde gegevens en probeer het opnieuw.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (onSubmit) {
        await onSubmit({
          amount: typeof amount === "number" ? amount : 0,
          purpose,
          note,
          recurring,
          name,
          email,
        });
        setLoading(false);
      }
    } catch (error_: unknown) {
      setLoading(false);
      setError(
        error_ instanceof Error
          ? error_.message
          : "Er ging iets mis bij het starten van de betaling.",
      );
    }
  }

  // CSS custom properties for theming
  const themeStyles = {
    "--donation-primary": `var(--${primaryColor})`,
    "--donation-text": `var(--${textColor})`,
    "--donation-bg": `var(--${backgroundColor})`,
  } as React.CSSProperties;

  // Clean, static class names
  const fieldClasses =
    "block w-full rounded-xl border bg-white/90 px-4 py-3 text-[15px] leading-tight placeholder-neutral-400 focus:outline-none focus:ring-2 text-boldness";

  const pillBaseClasses =
    "inline-flex items-center justify-center rounded-full border text-sm px-3 py-2 transition active:scale-[.98]";

  const labelClasses = "text-xs font-medium uppercase tracking-wide";

  return (
    <section
      className={cn("relative isolate", className)}
      id="donation-form"
      aria-label="Donatieformulier"
      style={themeStyles}
    >
      <div
        className="mx-auto w-full max-w-xl rounded-3xl border border-black/5 p-5 shadow-xl ring-1 ring-black/5 sm:p-6"
        style={{ backgroundColor: "var(--donation-bg)" }}
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between space-x-8">
          <div>
            <div className="hidden select-none sm:block">
              <div className="h-2 w-20 rounded-full bg-black/10">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: step === 1 ? "50%" : "100%",
                    backgroundColor: "var(--donation-primary)",
                  }}
                />
              </div>
              <p
                className="mt-1 text-[10px] uppercase"
                style={{ color: "var(--donation-text)" }}
              >
                Stap {step}/2
              </p>
            </div>
            <h2
              className="text-2xl font-bold tracking-tight"
              style={{ color: "var(--donation-text)" }}
            >
              {title}
            </h2>
            {description && (
              <div
                className="mt-1 text-sm"
                style={{ color: "var(--donation-text)" }}
              >
                {typeof description === "string" ? (
                  <p>{description}</p>
                ) : (
                  description
                )}
              </div>
            )}
          </div>

          {image && (
            <div className="hidden select-none sm:block">
              <Image
                src={image.src}
                alt={image.alt}
                width={image.width || 150}
                height={image.height || 150}
                className="m-2 rounded-md"
              />
            </div>
          )}
        </div>

        {/* Card body */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div
                key="step1"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="space-y-5"
              >
                {/* Amount buttons */}
                <div>
                  <p
                    className={labelClasses}
                    style={{ color: "var(--donation-text)" }}
                  >
                    Kies bedrag
                  </p>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {amounts.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => {
                          setAmount(v);
                          setTouchedFields((previous) => ({
                            ...previous,
                            amount: true,
                          }));
                        }}
                        className={cn(
                          pillBaseClasses,
                          amount === v
                            ? "cursor-default"
                            : "border-black/10 bg-white text-black hover:cursor-pointer hover:border-black/20",
                        )}
                        style={
                          amount === v
                            ? {
                              backgroundColor: "var(--donation-primary)",
                              borderColor: "var(--donation-primary)",
                              color: "var(--donation-text)",
                            }
                            : undefined
                        }
                        aria-pressed={amount === v}
                      >
                        € {v}
                      </button>
                    ))}
                  </div>

                  {/* Custom amount */}
                  <div className="mt-3 space-y-2">
                    <div
                      className="flex items-center gap-2"
                      style={{ color: "var(--donation-text)" }}
                    >
                      <span className="text-sm">of</span>
                      <label className="sr-only" htmlFor="custom-amount">
                        Ander bedrag
                      </label>
                      <input
                        id="custom-amount"
                        inputMode="decimal"
                        pattern="[0-9]*"
                        className={cn(fieldClasses, "max-w-[180px]")}
                        style={{
                          borderColor: getAmountError()
                            ? "var(--error, #ef4444)"
                            : "var(--donation-primary)",
                        }}
                        placeholder="Ander bedrag"
                        value={amount === "" ? "" : amount}
                        onChange={(e) => {
                          const v = e.target.value.replaceAll(/[^\d]/g, "");
                          setAmount(v ? Number(v) : "");
                          setTouchedFields((previous) => ({
                            ...previous,
                            amount: true,
                          }));
                        }}
                        onBlur={() => {
                          setTouchedFields((previous) => ({
                            ...previous,
                            amount: true,
                          }));
                        }}
                      />
                    </div>
                    {getAmountError() && (
                      <p
                        className="mt-1 text-xs font-medium"
                        style={{ color: "var(--error, #ef4444)" }}
                      >
                        {getAmountError()}
                      </p>
                    )}
                  </div>
                </div>

                {/* Purpose selection */}
                {donationPurposes.length > 1 && (
                  <div>
                    <p
                      className={labelClasses}
                      style={{ color: "var(--donation-text)" }}
                    >
                      Bestemming *
                    </p>
                    <label className="sr-only" htmlFor="purpose">
                      Bestemming
                    </label>
                    <select
                      id="purpose"
                      className={cn(fieldClasses, "mt-2")}
                      style={{
                        borderColor: getPurposeError()
                          ? "var(--error, #ef4444)"
                          : "var(--donation-primary)",
                      }}
                      value={purpose}
                      onChange={(e) => {
                        setPurpose(e.target.value);
                        setTouchedFields((previous) => ({
                          ...previous,
                          purpose: true,
                        }));
                      }}
                      onBlur={() => {
                        setTouchedFields((previous) => ({
                          ...previous,
                          purpose: true,
                        }));
                      }}
                    >
                      <option value="" disabled>
                        Kies een bestemming
                      </option>
                      {donationPurposes.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    {getPurposeError() && (
                      <p
                        className="mt-1 text-xs font-medium"
                        style={{ color: "var(--error, #ef4444)" }}
                      >
                        {getPurposeError()}
                      </p>
                    )}
                  </div>
                )}
                {/* Optional note */}
                <div>
                  <p
                    className={labelClasses}
                    style={{ color: "var(--donation-text)" }}
                  >
                    Persoonlijk bericht (optioneel)
                  </p>
                  <label className="sr-only" htmlFor="note">
                    Persoonlijk bericht
                  </label>
                  <input
                    id="note"
                    className={cn(fieldClasses, "mt-2")}
                    style={{
                      borderColor: "var(--donation-primary)",
                    }}
                    placeholder="Bijv. 'voor een specifiek doel'"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>

                {/* Recurring toggle */}
                <div>
                  <p
                    className={labelClasses}
                    style={{ color: "var(--donation-text)" }}
                  >
                    Frequentie
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setRecurring("oneTime")}
                      className={cn(
                        pillBaseClasses,
                        recurring === "oneTime"
                          ? "cursor-default text-white"
                          : "border-black/10 bg-white text-black hover:cursor-pointer hover:border-black/20",
                      )}
                      style={
                        recurring === "oneTime"
                          ? {
                            borderColor: "var(--donation-primary)",
                            backgroundColor: "var(--donation-primary)",
                          }
                          : undefined
                      }
                    >
                      Eenmalig
                    </button>
                    <button
                      type="button"
                      onClick={() => setRecurring("monthly")}
                      className={cn(
                        pillBaseClasses,
                        recurring === "monthly"
                          ? "cursor-default text-white"
                          : "border-black/10 bg-white text-black hover:cursor-pointer hover:border-black/20",
                      )}
                      style={
                        recurring === "monthly"
                          ? {
                            borderColor: "var(--donation-primary)",
                            backgroundColor: "var(--donation-primary)",
                          }
                          : undefined
                      }
                    >
                      Maandelijks
                    </button>
                  </div>
                </div>

                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setTouchedFields((previous) => ({
                        ...previous,
                        amount: true,
                        purpose: true,
                      }));
                      if (isValidStep1()) {
                        setStep(2);
                      }
                    }}
                    className={cn(
                      "w-full rounded-2xl px-4 py-3 text-base font-semibold text-white shadow-sm transition hover:brightness-110",
                      isValidStep1()
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-60",
                    )}
                    style={{
                      backgroundColor: "var(--donation-primary)",
                    }}
                  >
                    Volgende stap
                  </button>
                  {!isValidStep1() && touchedFields.amount && (
                    <p
                      className="mt-2 text-center text-xs"
                      style={{ color: "var(--donation-text)" }}
                    >
                      Controleer de ingevulde gegevens
                    </p>
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="space-y-4"
              >
                <div className="space-y-3">
                  <div>
                    <label
                      className={labelClasses}
                      style={{ color: "var(--donation-text)" }}
                      htmlFor="name"
                    >
                      Naam
                    </label>
                    <input
                      id="name"
                      className={fieldClasses}
                      style={{
                        borderColor: getNameError()
                          ? "var(--error, #ef4444)"
                          : "var(--donation-primary)",
                      }}
                      autoComplete="name"
                      placeholder="Jouw Naam"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setTouchedFields((previous) => ({
                          ...previous,
                          name: true,
                        }));
                      }}
                      onBlur={() => {
                        setTouchedFields((previous) => ({
                          ...previous,
                          name: true,
                        }));
                      }}
                    />
                    {getNameError() && (
                      <p
                        className="mt-1 text-xs font-medium"
                        style={{ color: "var(--error, #ef4444)" }}
                      >
                        {getNameError()}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      className={labelClasses}
                      style={{ color: "var(--donation-text)" }}
                      htmlFor="email"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className={fieldClasses}
                      style={{
                        borderColor: getEmailError()
                          ? "var(--error, #ef4444)"
                          : "var(--donation-primary)",
                      }}
                      autoComplete="email"
                      placeholder="voorbeeld@email.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setTouchedFields((previous) => ({
                          ...previous,
                          email: true,
                        }));
                      }}
                      onBlur={() => {
                        setTouchedFields((previous) => ({
                          ...previous,
                          email: true,
                        }));
                      }}
                    />
                    {getEmailError() && (
                      <p
                        className="mt-1 text-xs font-medium"
                        style={{ color: "var(--error, #ef4444)" }}
                      >
                        {getEmailError()}
                      </p>
                    )}
                  </div>

                  {/* Info text about personal/business gifts */}
                  <p
                    className="text-sm italic"
                    style={{ color: "var(--donation-text)" }}
                  >
                    Je gift kan persoonlijk of zakelijk zijn.
                  </p>
                </div>

                {error && (
                  <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                    {error}
                  </p>
                )}

                <div className="flex flex-wrap gap-2 pt-1 md:flex-nowrap">
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setError(null);
                    }}
                    className="w-full cursor-pointer rounded-2xl border px-4 py-3 text-base font-medium hover:bg-neutral-50"
                    style={{
                      backgroundColor: "var(--donation-primary)",
                      borderColor: "var(--donation-primary)",
                      color: "var(--donation-text)",
                    }}
                  >
                    Terug
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={cn(
                      "w-full rounded-2xl px-4 py-3 text-base font-semibold text-white shadow-sm transition hover:brightness-110",
                      loading
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer",
                    )}
                    style={{
                      backgroundColor: "var(--donation-primary)",
                    }}
                  >
                    {loading
                      ? "Bezig…"
                      : `Geef ${recurring === "monthly" ? "maandelijks" : "éénmalig"} € ${amount || 0}`}
                  </button>
                </div>

                {/* Legal hint */}
                <p
                  className="pt-1 text-[11px]"
                  style={{ color: "var(--donation-text)" }}
                >
                  Door te geven ga je akkoord met verwerking van je gegevens
                  t.b.v. de betaling via Mollie.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </section>
  );
}
